/* eslint-disable max-lines-per-function */
import { faArrowLeft, faArrowRight } from '@fortawesome/pro-regular-svg-icons'
import { moment } from '@seiue/moment'
import { Button, Modal, Radio, Toast, DatePicker } from '@seiue/ui'
import { compact, isArray, isNil } from '@seiue/util'
import { Form } from 'antd'
import React, { FC, useEffect, useRef } from 'react'

import { $t } from 'packages/locale'
import {
  MethodEnum,
  Notification,
  NotificationReq,
  ReflectionRoleEnum,
  UserForegroundDomainEnum,
  notificationApi,
} from 'packages/sdks-next/growp'

import { ContentCard } from '@/components/ContentCard'
import { ModalLayout } from '@/components/ModalLayout'
import { setSelectedReceiverIds as setSelectedIds } from '@/components/NoticeEditor/data'
import {
  NotificationAttachment,
  attachment,
  receivers,
  setAttachment,
  setVariables,
  setReceivers,
  setReflectionDomain,
  variables,
} from '@/components/NoticeEditor/variablesMenuConfig'
import { SelectReflectionContent } from '@/components/SelectReflectionContent'
import { WaringText } from '@/components/Styled'
import { TaskHeaderLeft } from '@/components/TaskHeaderLeft'

import { NewsEditor } from './NewsEditor'
import { RichTextEditorRef } from 'packages/components/RichText/Editor/types'

export type HotNewsModalProps = {
  notification?: Notification
  onClose: () => void
  reload: () => void
  /**
   * 是否只编辑内容
   */
  justEditContent?: boolean
}

/**
 * 编辑通知弹窗
 *
 * @param param0 - 参数
 * @param param0.notification - 通知
 * @param param0.onClose - 关闭弹窗
 * @param param0.reload - 刷新
 * @param param0.justEditContent - 只编辑内容
 * @returns 组件
 */
export const EditHotNewsModal: FC<{
  notification?: Notification
  onClose: () => void
  reload: () => void
  justEditContent?: boolean
}> = ({ notification, onClose, reload, justEditContent }) => {
  // 创建资讯本质上是创建通知， activityId 传 0 代表资讯类型的通知
  const activityId = 0
  const isEdit = !!notification

  const richtextRef = useRef<RichTextEditorRef>(null)

  const [selectedRowKeys, setSelectedRowKeys] = React.useState<number[]>(
    notification?.rids || []
  )

  const [scopes, setScopes] = React.useState<{
    teacherIds: number[]
    studentIds: number[]
    ids: number[]
  }>({
    teacherIds:
      notification?.receverRoles?.find(
        (item) => item.role === ReflectionRoleEnum.Teacher
      )?.rids || [],
    studentIds:
      notification?.receverRoles?.find(
        (item) => item.role === ReflectionRoleEnum.Student
      )?.rids || [],
    ids: notification?.rids || [],
  })

  // 获取用户信息，变量
  const { data: fetchReceivers, loading: loadingReceiver } =
    notificationApi.useQueryNotificationReflections(
      {
        id: notification?.id || 0,
        query: {
          paginated: 0,
        },
      },
      {
        disable: !notification?.id,
      }
    )

  const [currentStep, setCurrentStep] = React.useState<number>(0)
  const [validField, setValidField] = React.useState<boolean>(false)
  const [isDirty, setIsDirty] = React.useState<boolean>(false)
  const [publishedAt, setPublishedAt] = React.useState<string | undefined>(
    notification?.method === MethodEnum.Draft
      ? undefined
      : notification?.publishedAt || moment().format('YYYY-MM-DD HH:mm:00')
  )

  const [form] = Form.useForm<{
    title: string
    content: string
    isLink?: boolean
  }>()

  const [method, setMethod] = React.useState<MethodEnum>(
    notification?.method || MethodEnum.Now
  )

  const { api: createNotification } = notificationApi.useCreateNotification({
    reload: [notificationApi.queryNotifications],
  })

  const { api: updateNotification } = notificationApi.useUpdateNotification({
    reload: [notificationApi.queryNotifications],
  })

  const onSelectedRowKeysChange = (selectedKeys: React.Key[]) => {
    setIsDirty(true)
    setSelectedRowKeys(selectedKeys as number[])
  }

  useEffect(() => {
    setReflectionDomain(UserForegroundDomainEnum.Formal)
    setSelectedIds(selectedRowKeys)
  }, [selectedRowKeys])

  useEffect(() => {
    const setting = notification?.setting as
      | { variables?: string[]; isLink?: boolean }
      | undefined

    const variablesColumns = setting?.variables || []

    setVariables(variablesColumns)

    setAttachment(
      // 后端对 object 类型的数据默认值给了个 []
      isArray(notification?.attachment) && !notification?.attachment.length
        ? undefined
        : ((notification?.attachment || undefined) as
            | NotificationAttachment
            | undefined)
    )
    setReceivers(
      compact(
        fetchReceivers?.map((r) =>
          !r.reflection?.id
            ? undefined
            : {
                rid: r.reflection?.id,
                id: r.id,
                variables: r.variables,
                schoolId: r.schoolId,
              }
        )
      )
    )
    form.setFieldValue(
      'isLink',
      // 如果是创建时，则默认为 true，如果是编辑，则判断之前有没有存 isLink 数据，存了则用 isLink，没有存则是之前创建的数据，之前创建的都是文本资讯，这时候 isLink 默认为 false
      !isEdit ? true : !isNil(setting?.isLink) ? setting?.isLink : false
    )
  }, [notification, fetchReceivers])

  const close = () => {
    setVariables([])
    setAttachment(undefined)
    setSelectedIds([])
    setReceivers([])
    setReflectionDomain(UserForegroundDomainEnum.Informal)
    onClose()
  }

  const isFirstStep = currentStep === 0

  const onCancel = () => {
    if (isDirty) {
      Modal.confirm({
        title: justEditContent
          ? $t('确定要离开当前页面吗？')
          : '确定离开该页面吗？',
        content: justEditContent
          ? $t('你将丢失尚未保存的资讯内容？')
          : '正在离开该页面，离开后修改数据将被清空；确认要离开吗？',
        onOk: () => {
          close()
        },
      })
    } else {
      close()
    }
  }

  const realInFirstStep = isFirstStep && !justEditContent

  return (
    <ModalLayout
      content={
        <>
          <ContentCard
            contentStyle={{
              padding: realInFirstStep ? 0 : 32,
              minHeight: form.getFieldValue('isLink') ? 0 : 200,
              display: 'flex',
            }}
          >
            {realInFirstStep ? (
              <SelectReflectionContent
                setScopes={setScopes}
                scopes={scopes}
                setIsChange={setIsDirty}
                setSelectedIds={onSelectedRowKeysChange}
                style={{ height: 'calc(100vh - 256px)' }}
                scroll={{ x: '1300', y: 'calc(100vh - 437px)' }}
              />
            ) : (
              <NewsEditor
                form={form}
                notification={notification}
                onValuesChange={() => {
                  setIsDirty(true)
                }}
                justEditContent={justEditContent}
                ref={richtextRef}
              />
            )}
          </ContentCard>
          {justEditContent ? null : (
            <ContentCard style={{ textAlign: 'center' }}>
              {!isFirstStep ? (
                <>
                  <Form.Item
                    label={'发送时间'}
                    required={true}
                    style={{ textAlign: 'left', marginBottom: 20 }}
                  >
                    <Radio.Group
                      defaultValue={MethodEnum.Now}
                      options={[
                        { label: '立即发送', value: MethodEnum.Now },
                        { label: '定时发送', value: MethodEnum.Future },
                        { label: '暂不发送', value: MethodEnum.Draft },
                      ]}
                      value={method}
                      onChange={(e) => {
                        setIsDirty(true)
                        setMethod(e.target.value)
                        setValidField(false)
                        if (e.target.value === 'now') {
                          setPublishedAt(moment().format('YYYY-MM-DD HH:mm:00'))
                        } else {
                          setPublishedAt(undefined)
                        }
                      }}
                    />
                  </Form.Item>
                  {method === MethodEnum.Future ? (
                    <Form.Item style={{ textAlign: 'left', marginLeft: 88 }}>
                      <DatePicker
                        style={{ width: 320 }}
                        showTime={true}
                        onChange={(_, dateString) => {
                          setIsDirty(true)
                          setPublishedAt(
                            moment(dateString).format('YYYY-MM-DD HH:mm:00')
                          )
                          setValidField(false)
                        }}
                        value={publishedAt ? moment(publishedAt) : undefined}
                      />
                      {validField && !publishedAt ? (
                        <WaringText>{'请选择定时发送时间'}</WaringText>
                      ) : null}
                    </Form.Item>
                  ) : null}
                </>
              ) : null}
              <Button
                onClick={() => {
                  if (isFirstStep && !selectedRowKeys.length) {
                    Toast.warning('请先选择成员')
                    return
                  }
                  setCurrentStep(isFirstStep ? 1 : 0)
                }}
                iconRight={isFirstStep ? faArrowRight : undefined}
                icon={!isFirstStep ? faArrowLeft : undefined}
              >
                {isFirstStep ? '下一步' : '上一步'}
              </Button>
            </ContentCard>
          )}
        </>
      }
      title={
        <TaskHeaderLeft
          title={isEdit ? '编辑资讯' : '新建资讯'}
          onClose={onCancel}
        />
      }
      stepsParams={
        justEditContent
          ? undefined
          : {
              current: currentStep,
              items: [{ title: '选择成员' }, { title: '撰写资讯' }],
              onChange: () => {
                if (isFirstStep && !selectedRowKeys.length) {
                  Toast.warning('请先选择成员')
                  return
                }
                setCurrentStep(isFirstStep ? 1 : 0)
              },
            }
      }
      headerRight={
        realInFirstStep ? null : (
          <>
            {justEditContent ? (
              <Button onClick={onCancel}>{$t('取消')}</Button>
            ) : null}
            <Button
              type="primary"
              onClick={() => {
                if (loadingReceiver) {
                  Toast.warning($t('数据正在加载，请稍侯。'))
                  return
                }
                setValidField(true)
                form.validateFields().then(async (values) => {
                  if (method === MethodEnum.Future && !publishedAt) {
                    return
                  }
                  const isLink = form.getFieldValue('isLink')
                  // 纯文本
                  const plainText = richtextRef.current?.getPlainText?.()

                  const receiversRes = selectedRowKeys.map((rid) => {
                    const existReceive = receivers?.find((r) => r.rid === rid)
                    if (existReceive) {
                      return { rid, variables: existReceive.variables }
                    }
                    return {
                      rid,
                      variables: '',
                    }
                  })

                  const payload: NotificationReq = {
                    method,
                    title: values.title,
                    content: values.content,
                    publishedAt,
                    reflectionDomain: UserForegroundDomainEnum.Formal,
                    withdrawReason: '',
                    setting: {
                      plainText,
                      variables,
                      isLink,
                    },
                    receivers: receiversRes,
                    attachment,
                    activityId,
                  }

                  if (isEdit) {
                    await updateNotification({
                      id: notification.id,
                      payload,
                    })

                    Toast.success('更新成功')
                    close()
                  } else {
                    await createNotification(payload)

                    Toast.success('发布成功')
                    close()
                  }

                  reload()
                })
              }}
            >
              {justEditContent ? $t('保存修改') : isEdit ? '更新' : '发送'}
            </Button>
          </>
        )
      }
    />
  )
}
